import React, { useEffect, useRef, useState } from 'react';
import { Post, Upload } from '../../models/post';
import { ChildProfile } from '../../models/childProfile';
import ReportView from './ReportView';
import ReviewPublishOverlay, { ALLOW_EDITING_PUBLISHED_POSTS } from '../ReviewPublishOverlay';
import ActionPanel from './ActionPanel';
import GenerateReportPanel, { ExistingReportInfo } from './GenerateReportPanel';
import { ReportDataService } from '../../services/reportDataService';
import { ReportProvider } from '../../context/ReportContext';
import { useAppState } from '../../context/AppStateContext';
import { formatDateFullWithOrdinal, postStamp } from '../../services/sharedService';
import { resolveHeaderField, ReportFieldContext } from '../../services/reportFieldResolvers';
import { ReportTemplate, STOCK_WEEKLY_TEMPLATE, templateNeedsAttendance } from '../../models/reportTemplate';
import { AttendanceStats, fetchAttendanceStats } from '../../services/attendanceStatsService';
import { ORG_CONFIG_DEFAULTS } from '../../models/orgConfig';
import api from '../../api/axios';
import './reportDetails.scss';
import HelpAsk from '../../help/HelpAsk';

type StatusFilter = 'all' | 'draft' | 'published';
type RightPane = 'posts' | 'details' | 'preview';

// The measure the report is laid out at whenever the pane is too narrow to
// give it that much. Below this the preview stops reflowing and starts
// zooming: the page keeps this layout — the one the PDF will actually have —
// and is simply displayed smaller. Reflowing instead produced a preview of a
// document that doesn't exist, which is worse than a small one.
// 560 rather than 600 since .report-container's padding came down from 3rem to
// 1.5rem: the same content now fits in a narrower page, so zooming starts
// later than it used to.
const REPORT_LAYOUT_WIDTH = 560;

// How far that will go. Past this the type is too small to judge anything by,
// so the preview gives up its pane and becomes a tab, where it gets the full
// width back.
const MIN_PREVIEW_ZOOM = 0.6;

// What the two panes need side by side: the smallest the preview can be shown
// at (600 × 0.6) plus the workspace column's 540px floor.
//
// Measured against the room this workspace has actually been given, not the
// viewport. The same window width means different space depending on whether
// the app menu is pinned (250px) or a rail (76px), so no viewport breakpoint
// can be right for both; this reads the truth and self-corrects whatever the
// menu does. Nothing becomes unreachable at any width.
const BOTH_PANES_MIN = Math.round(REPORT_LAYOUT_WIDTH * MIN_PREVIEW_ZOOM) + 540;

// A floor under the whole arrangement, independent of the measurement above.
// The workspace can technically clear BOTH_PANES_MIN on a small screen once
// the menu has railed, but by then everything around it is cramped and two
// panes is the wrong shape for the window regardless of whether they fit.
// Below this the preview is a tab, full stop.
const FORCE_TAB_QUERY = '(max-width: 1199px)';

interface BuildReportPanelProps {
  /** Every post in the report period — drafts and published. */
  posts: Post[];
  childName: string;
  /** Full profile — enables alias/initials/reference header fields. */
  child?: ChildProfile | null;
  childId: string | null;
  /** Period bounds (YYYY-MM-DD). */
  startDate?: string | null;
  endDate?: string | null;
  /** The template selected in the Reports toolbar. */
  template?: ReportTemplate;
  onReloadPosts: (skipConfirm?: boolean) => Promise<void>;
  onNotice: (msg: string) => void;
}

/**
 * The photos on a review-queue card.
 *
 * The sheet is the same width on every card, so the writing beside it starts
 * at the same point all the way down the queue. What the photos may do is
 * divide that width: one image across the whole sheet, or — when the first is
 * clearly portrait and there is a second to pair it with — two side by side
 * filling the same space. A tall photo in a wide box would otherwise be shown
 * as a vertical slice through its middle, `object-fit: cover` having discarded
 * the rest.
 *
 * Only the first image is measured. It's the one the card leads with, and
 * deciding from all of them would mean the layout changing as each arrived.
 */
const ReviewPhotoSheet: React.FC<{ uploads: Upload[]; eager: boolean }> = ({ uploads, eager }) => {
  const [split, setSplit] = useState(false);

  // Two are always rendered, so pairing doesn't pop a second tile into place
  // once the measurement lands; CSS hides the spare when the sheet isn't
  // split. The count in the masthead still reports the post's real total.
  const shots = uploads.slice(0, 2);

  const measure = (img: HTMLImageElement) => {
    if (!img.naturalWidth || !img.naturalHeight) return;
    // Deliberately loose — only a clearly portrait photo pairs up. Anything
    // near square is served perfectly well across the full width, and a
    // borderline case shouldn't make one card in the queue look unlike its
    // neighbours.
    const isPortrait = img.naturalWidth / img.naturalHeight <= 0.8;
    setSplit(isPortrait && uploads.length >= 2);
  };

  return (
    <div className={`post-card__sheet${split ? ' post-card__sheet--split' : ''}`}>
      {shots.map((u, idx) => (
        <img
          key={u.id}
          src={u.url || u.thumbnail_url}
          alt={u.file_name}
          loading={eager ? 'eager' : 'lazy'}
          decoding="async"
          onLoad={idx === 0 ? (e) => measure(e.currentTarget) : undefined}
          style={u.excluded_from_report ? { opacity: 0.45 } : undefined}
          title={u.excluded_from_report ? 'Excluded from the report' : undefined}
        />
      ))}
    </div>
  );
};

/**
 * The single Build Report workspace: the report preview builds up on the left
 * as posts are approved, and stays mounted from first review through to PDF
 * generation. The right-hand column switches between two panes:
 *
 *  - Posts — every post in the period, filterable by status. Clicking one
 *    opens the ReviewPublishOverlay; after publishing, the next draft opens
 *    automatically so the reviewer works straight through the period.
 *  - Report Details — the template's generation-time fields plus the
 *    Generate button (the old Generate Report tab's action column).
 *
 * Pane and status filter both default to wherever the work is: drafts
 * outstanding → Posts pane filtered to drafts; everything published → Report
 * Details. The defaults re-derive whenever the child or period changes, but
 * an explicit click on either control sticks for the current period.
 */
const BuildReportPanel: React.FC<BuildReportPanelProps> = ({
  posts,
  childName: defaultChildName,
  child,
  childId,
  startDate,
  endDate,
  template = STOCK_WEEKLY_TEMPLATE,
  onReloadPosts,
  onNotice,
}) => {
  const { userRoleIds, roles, selectedOrganisation, orgConfig, activeUser } = useAppState();
  // /reports is already role-gated in Home; this guard is defence in depth —
  // without the Reporting role the right column vanishes entirely.
  const canReport = userRoleIds.includes(roles.Reporting);

  // User overrides for the right-hand pane and the status filter. null means
  // "derive from the drafts outstanding"; a click pins the choice until the
  // child or period changes.
  const [paneChoice, setPaneChoice] = useState<RightPane | null>(null);

  // Observed on the split-screen container, whose width is set by the layout
  // around it and never by its own children — so folding the preview away
  // can't change the measurement that folded it, and it can't oscillate.
  const splitRef = useRef<HTMLDivElement>(null);
  const [workspaceTooTight, setWorkspaceTooTight] = useState(false);
  useEffect(() => {
    const el = splitRef.current;
    if (!el) return;
    const observer = new ResizeObserver(([entry]) => {
      setWorkspaceTooTight(entry.contentRect.width < BOTH_PANES_MIN);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const [windowTooNarrow, setWindowTooNarrow] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(FORCE_TAB_QUERY).matches
  );
  useEffect(() => {
    const mq = window.matchMedia(FORCE_TAB_QUERY);
    const onChange = (e: MediaQueryListEvent) => setWindowTooNarrow(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  // Either reason is enough: not enough room measured, or a window too small
  // for the split to be the right shape whatever the measurement says.
  const isNarrow = workspaceTooTight || windowTooNarrow;

  // How much the preview has to shrink to fit the pane it's been given. 1 while
  // there's room for the report's full layout; below that it's scaled, not
  // reflowed. Re-attached when the pane appears or disappears, since it isn't
  // rendered at all in the narrow layout.
  const previewRef = useRef<HTMLDivElement>(null);
  const [previewZoom, setPreviewZoom] = useState(1);
  useEffect(() => {
    const el = previewRef.current;
    if (!el) return;
    const observer = new ResizeObserver(([entry]) => {
      const available = entry.contentRect.width;
      setPreviewZoom(
        available >= REPORT_LAYOUT_WIDTH
          ? 1
          : Math.max(MIN_PREVIEW_ZOOM, available / REPORT_LAYOUT_WIDTH)
      );
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [isNarrow]);

  // In the tab the preview has the whole workspace, so it renders at full size.
  const zoom = isNarrow ? 1 : previewZoom;

  const [filterChoice, setFilterChoice] = useState<StatusFilter | null>(null);
  const [reviewPostId, setReviewPostId] = useState<number | null>(null);

  // ── Report state shared by the preview and the Report Details pane ───────
  const [childName, setChildName] = useState<string>(defaultChildName || '');
  const [reportDate, setReportDate] = useState<string>(endDate || '');
  // Header renders unconditionally; no toggle is exposed at present.
  const [showHeader, setShowHeader] = useState<boolean>(true);
  const [pdfPassword, setPdfPassword] = useState<string>('');
  // Generation-time entries for manual-editable fields (see ReportContext for
  // the touched/untouched semantics). Lives here so entries survive switching
  // between the Posts and Report Details panes.
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});
  const setFieldValue = (key: string, value: string) =>
    setFieldValues(prev => ({ ...prev, [key]: value }));

  useEffect(() => {
    if (defaultChildName) setChildName(defaultChildName);
  }, [defaultChildName]);

  // Period navigation drives the report date to the period's end.
  useEffect(() => {
    if (endDate) setReportDate(endDate);
  }, [endDate]);

  // ── Existing-report lookup ───────────────────────────────────────────────
  // A new child/period/template is a different report identity: clear the
  // generation state (so one child's entries never leak into another's
  // report), then hydrate it from the stored report record — the field
  // values and password used last time come back pre-filled, so
  // regenerating after a post change doesn't mean re-typing them.
  const [existingReport, setExistingReport] = useState<ExistingReportInfo | null>(null);
  const [checkingExisting, setCheckingExisting] = useState(false);
  const templateId = template.id ?? 'stock';

  const reportIdentity = `${childId}|${startDate}|${endDate}|${templateId}`;
  const lastIdentity = useRef<string | null>(null);
  // Which identity's stored report we've actually loaded into state. Tracked
  // separately from lastIdentity because "the identity changed" is NOT the same
  // question as "have we hydrated yet": on remount the first lookup fires while
  // `posts` is still empty, and is then cancelled the moment they arrive. If the
  // follow-up treated itself as a mere refresh it would refuse to hydrate, and
  // an existing report would read as "Not generated" until the page reloaded.
  const hydratedFor = useRef<string | null>(null);

  // Staleness is the server's answer, so it has to be re-asked when something
  // changes it — publishing, unpublishing, or holding a photo back all leave the
  // report identity untouched, so keying the lookup on identity alone left the
  // press bay insisting a report was up to date until the page was reloaded.
  // These are exactly the actions that reload `posts`, so a signature over the
  // report-relevant shape of them is the trigger. (The old client-side check was
  // live for free because it derived from this same local state — this keeps
  // that immediacy while still having one source of truth.)
  const postsSignature = posts
    .map(p => [
      p.id,
      p.is_published ? 1 : 0,
      p.published_at ?? '',
      // Which photos the report would actually render, and in which variant —
      // excluding one, deleting one, or switching a face-blurred version in or
      // out all change the document, so all of them have to move this string.
      (p.uploads ?? [])
        .filter(u => !u.excluded_from_report && !u.to_delete)
        .map(u => `${u.id}${u.display_modified ? 'm' : ''}`)
        .join(','),
    ].join(':'))
    .join('|');

  useEffect(() => {
    // A new child/period/template is a different report identity: clear the
    // generation state so one child's entries never leak into another's. A mere
    // post change must NOT do that — the field values and password the user has
    // typed have to survive publishing a post.
    const identityChanged = lastIdentity.current !== reportIdentity;
    lastIdentity.current = reportIdentity;
    if (identityChanged) {
      setFieldValues({});
      setPdfPassword('');
      setExistingReport(null);
      hydratedFor.current = null;
    }
    if (!childId || !startDate || !endDate) return;
    let cancelled = false;
    if (hydratedFor.current !== reportIdentity) setCheckingExisting(true);
    ReportDataService.getExistingReport(parseInt(childId), startDate, endDate, templateId)
      .then((r) => {
        if (cancelled) return;
        // Re-read the ref here rather than trusting a value captured when the
        // effect ran: whichever lookup lands FIRST does the hydrating, so a
        // cancelled predecessor can't leave the panel empty.
        if (hydratedFor.current !== reportIdentity) {
          hydratedFor.current = reportIdentity;
          setExistingReport({
            url: r.url,
            createdAt: r.createdAt ?? null,
            createdByName: r.createdByName ?? null,
            // Kept beside the URL so the inline viewer can open a protected
            // report even after the password input is edited without a regenerate.
            password: r.password ?? undefined,
            // The entries the stored PDF was built with, kept separate from the
            // fieldValues state below — that state is the edit buffer, so this
            // is the only surviving record of what the file actually used.
            fieldValues: r.fieldValues ?? {},
            // Server-computed, so the press bay and the History pill give the
            // same account of the same report.
            staleReasons: r.staleReasons ?? [],
          });
          setPdfPassword(r.password ?? '');
          if (r.fieldValues) setFieldValues(r.fieldValues);
          return;
        }
        // Already hydrated: this lookup was triggered by the posts moving, so
        // take the new staleness and leave everything the user may have edited
        // alone.
        setExistingReport(prev => (prev ? { ...prev, staleReasons: r.staleReasons ?? [] } : prev));
      })
      .catch(() => {
        // 404 means no report yet — not an error worth surfacing. hydratedFor
        // stays unset so a later lookup still hydrates if one gets generated.
      })
      .finally(() => {
        // Unconditional: gating this on the identity having changed stranded the
        // flag at true whenever the first lookup was cancelled.
        if (!cancelled) setCheckingExisting(false);
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportIdentity, postsSignature]);

  // A new child or period is a new report — pane/filter defaults re-derive.
  useEffect(() => {
    setPaneChoice(null);
    setFilterChoice(null);
    setReviewPostId(null);
  }, [childId, startDate, endDate]);

  // ── Attendance stats (Phase 2) ───────────────────────────────────────────
  // Fetched only when the template references attendance (a section or a
  // derived field), and re-fetched on child/period/template change. One fetch
  // powers both the attendance-derived header fields and the section, so the
  // header numbers and the charts can never disagree.
  const [attendance, setAttendance] = useState<AttendanceStats | null>(null);
  const needsAttendance = templateNeedsAttendance(template.config);
  const graceMinutes = orgConfig?.attendanceGraceMinutes ?? ORG_CONFIG_DEFAULTS.attendanceGraceMinutes;

  useEffect(() => {
    setAttendance(null);
    if (!needsAttendance || !childId || !startDate || !endDate) return;
    let cancelled = false;
    fetchAttendanceStats(api, parseInt(childId), startDate, endDate, graceMinutes)
      .then((stats) => { if (!cancelled) setAttendance(stats); });
    return () => { cancelled = true; };
  }, [needsAttendance, childId, startDate, endDate, graceMinutes]);

  // Chronological (ascending) regardless of the Posts page's sort order, so
  // the list mirrors the report's own ordering and the reviewer works from
  // the start of the report period to the end.
  const orderedPosts = [...posts].sort(
    (a, b) =>
      new Date(a.post_date).getTime() - new Date(b.post_date).getTime() || a.id - b.id
  );
  const publishedPosts = orderedPosts.filter(p => p.is_published);
  const draftPosts = orderedPosts.filter(p => !p.is_published);

  const filter: StatusFilter = filterChoice ?? (draftPosts.length > 0 ? 'draft' : 'all');
  // Posts, always. The pane used to flip to Report Details once everything was
  // published, purely so the Generate button could be reached — with the press
  // bay now pinned under both panes there's nothing to flip to, so the rule
  // collapses to the reviewer's actual job and Report Details becomes somewhere
  // you go on purpose.
  // Preview only exists as a pane while the screen is too narrow to give it
  // its own column; widening the window hands it back to the left and drops
  // the reviewer on Posts rather than a tab that no longer exists.
  const pane: RightPane =
    paneChoice === 'preview' && !isNarrow ? 'posts' : (paneChoice ?? 'posts');

  const listPosts =
    filter === 'draft' ? draftPosts :
    filter === 'published' ? publishedPosts :
    orderedPosts;

  // Looked up in the whole period, so the overlay stays open on a post the
  // current filter has stopped showing (publishing a draft under the Draft
  // filter). Its place in the *filtered* list is what the prev/next buttons
  // step through — -1 once it has dropped out, which disables them rather than
  // stepping somewhere arbitrary.
  const reviewPost = orderedPosts.find(p => p.id === reviewPostId) ?? null;
  const reviewIndex = listPosts.findIndex(p => p.id === reviewPostId);

  // Keep the open post in view in the queue behind the overlay. Stepping with
  // the overlay's prev/next walks the list without touching its scroll, so the
  // card being reviewed drifts off screen and is gone when the overlay closes.
  //
  // Measured rather than left to scrollIntoView, which is free to scroll every
  // ancestor including the page: this scrolls the queue by the least that shows
  // the whole card, and not at all if it's already visible. Same approach as
  // the Posts grid, without its wait on the drawer animation — this overlay
  // covers the pane instead of reflowing it, so the card is already where it
  // will end up.
  const queuePaneRef = useRef<HTMLDivElement>(null);
  const selectedCardRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!reviewPostId) return;
    const frame = requestAnimationFrame(() => {
      const card = selectedCardRef.current;
      const paneEl = queuePaneRef.current;
      if (!card || !paneEl) return;

      const cardBox = card.getBoundingClientRect();
      const paneBox = paneEl.getBoundingClientRect();
      if (paneBox.height === 0) return;   // queue isn't the visible sub-tab

      const margin = 12;
      let delta = 0;
      if (cardBox.top - margin < paneBox.top) {
        delta = cardBox.top - margin - paneBox.top;
      } else if (cardBox.bottom + margin > paneBox.bottom) {
        // A card too tall to fit whole is shown from its top rather than its end.
        delta = cardBox.height + margin * 2 > paneBox.height
          ? cardBox.top - margin - paneBox.top
          : cardBox.bottom + margin - paneBox.bottom;
      }
      if (!delta) return;

      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      paneEl.scrollBy({ top: delta, behavior: reduceMotion ? 'auto' : 'smooth' });
    });
    return () => cancelAnimationFrame(frame);
    // `pane` matters: the queue isn't mounted while Report Details is showing,
    // so coming back to it has to re-run this.
  }, [reviewPostId, pane]);

  // Generation-editable fields — manual and derived — drive the Report
  // Details inputs and the required-field gate on Generate. report_date is
  // excluded: the panel's dedicated Report Date input is already its edit
  // path, so a second input would just duplicate it.
  const editableFields = template.config.headerFields.filter(
    f => f.editableAtGeneration && f.key !== 'report_date'
  );

  // Untouched-input pre-fills: the template default for manual fields, the
  // live computed value for derived ones (recomputes as posts publish). A
  // fieldValues entry — a touched input — takes precedence everywhere.
  const resolverCtx: ReportFieldContext = {
    posts: publishedPosts,
    childName,
    child,
    reportDate: reportDate ? formatDateFullWithOrdinal(new Date(reportDate + 'T00:00').toISOString(), false) : undefined,
    startDate,
    endDate,
    orgName: orgConfig?.settingName || selectedOrganisation?.name,
    authorName: activeUser?.name,
    templateName: template.name,
    attendance,
  };
  const fieldDefaults: Record<string, string> = Object.fromEntries(
    editableFields.map(f => [f.key, resolveHeaderField(f, resolverCtx) ?? ''])
  );

  const handlePublished = async (msg: string) => {
    // Work out where to go next BEFORE the reload reshuffles the list: the
    // next unpublished post after the current one in visible list order,
    // wrapping to earlier drafts if the reviewer started mid-list.
    const visibleRemaining = listPosts.filter(p => !p.is_published && p.id !== reviewPostId);
    const idx = listPosts.findIndex(p => p.id === reviewPostId);
    const next =
      visibleRemaining.find(p => listPosts.indexOf(p) > idx) ?? visibleRemaining[0] ?? null;
    // "All published" is judged across the whole period, not just the
    // current filter — a hidden draft must not trigger the generate nudge.
    const allPublished = !orderedPosts.some(p => !p.is_published && p.id !== reviewPostId);
    onNotice(allPublished ? `${msg} All posts are published — the report is ready to generate.` : msg);
    await onReloadPosts(true);
    setReviewPostId(next ? next.id : null);
    // No pane switch on the last publish: the press bay is already on screen,
    // so the Generate button the reviewer needs is where it always was.
  };

  // The report as it currently stands. Rendered once and placed by width: its
  // own pane beside the workspace when there's room, or the Preview sub-tab
  // inside the column when there isn't. The sheet is separate from the captioned
  // wrapper below because only one of those placements wants a caption.
  const previewSheet = (
    <div
      className="report-container card"
      style={{
        flex: 1,
        margin: !canReport ? '0 auto' : undefined,
        // `zoom` rather than a transform: it affects layout, so the pane's
        // scrollbars and the card's own height stay honest instead of the page
        // being painted outside a box that still thinks it's full size.
        //
        // Nothing else is set. `zoom` resolves percentages against the
        // containing block divided by the factor, so the stylesheet's own
        // `width: 100%` already fills the pane exactly and lays the page out at
        // `paneWidth / zoom` — which is REPORT_LAYOUT_WIDTH by construction,
        // since that's where the factor came from. Pinning a pixel width here
        // instead left the page short by the pane's padding and scrollbar, and
        // dividing the percentage by the zoom overshot by the same factor
        // again, scrolling sideways. The browser had it right on its own.
        ...(zoom < 1 ? { zoom } : null),
      }}
    >
      <div style={{ flex: 1, overflowY: 'auto', paddingBottom: '30px' }}>
        <div className="card-body">
          {publishedPosts.length === 0 ? (
            <div className="text-center text-muted p-5">
              <i className="fas fa-file-circle-plus mb-3" style={{ fontSize: '2rem', opacity: 0.4 }} />
              <h5 className="font-primary">The report is empty</h5>
              <p className="mb-0" style={{ fontSize: '15px' }}>
                Review and publish posts from the list to add them to the report.
              </p>
            </div>
          ) : (
            <ReportView
              posts={publishedPosts}
              childName={childName}
              child={child}
              reportDate={reportDate ? formatDateFullWithOrdinal(new Date(reportDate + 'T00:00').toISOString(), false) : undefined}
              startDate={startDate}
              endDate={endDate}
              includeHeader={showHeader}
              logoUrl={selectedOrganisation?.logoUrl}
              orgName={orgConfig?.settingName || selectedOrganisation?.name}
              authorName={activeUser?.name}
              brandColour={selectedOrganisation?.brandColour}
              template={template.config}
              templateName={template.name}
              fieldValues={fieldValues}
              attendance={attendance}
            />
          )}
        </div>
      </div>
    </div>
  );

  // Captioned, for the pane beside the workspace: there the sheet is unlabelled
  // chrome-less paper that reads as the finished report, so it says what it is.
  // The Preview sub-tab doesn't get this — the tab itself is the label, and
  // repeating it under its own tab is furniture. Keeping the caption out of that
  // branch also leaves the sub-tab rendering the bare sheet exactly as it did
  // before, rather than inside a wrapper that changes its flex context.
  const previewNode = (
    <div className="report-preview">
      {/* Outside .report-container deliberately: not painted onto the sheet, and
          not shrunk by the zoom the sheet carries. */}
      <div className="report-preview__caption">
        <span className="report-preview__eyebrow">Live preview</span>
      </div>
      {previewSheet}
    </div>
  );

  return (
    <ReportProvider value={{ childName, setChildName, reportDate, setReportDate, showHeader, setShowHeader, pdfPassword, setPdfPassword, fieldValues, setFieldValue }}>
      <div className={`report-split-screen${isNarrow ? ' is-narrow' : ''}`} ref={splitRef}>
        {/* LEFT — the preview, when the screen is wide enough to sit it beside
            the workspace. Below that it moves into the Preview sub-tab. */}
        {/* Without the Reporting role there is no column to host the Preview
            tab, so the pane stays whatever the width. */}
        {(!isNarrow || !canReport) && (
          <div
            ref={previewRef}
            className="report-panel"
            style={canReport ? { flex: 1 } : { flex: 1, maxWidth: '100%', width: '100%' }}
          >
            {previewNode}
          </div>
        )}

        {/* RIGHT — switches between the review list and the generation fields */}
        {canReport && (
          <div className="build-report-column d-flex flex-column">
            {/* The sheet: everything above the press bay. The ivory gradient
                lives here rather than on the whole column so it finishes at
                exactly the bay's top edge — the bay is painted the gradient's
                end colour, so the two meet on one tone and the bar reads as
                the bottom of the sheet rather than a panel laid over it. */}
            <div className="build-report-column__sheet">
            {/* Sub-tabs = navigation (echoes the page tab strip); the status
                pill = filter. Different visual weight keeps the two rows from
                reading as one double-decker control. */}
            <div className="build-report-header">
              <div className="build-report-subtabs" role="group" aria-label="Switch between posts, report details and the preview">
                <button
                  type="button"
                  className={`build-report-subtab ${pane === 'posts' ? 'is-active' : ''}`}
                  onClick={() => setPaneChoice('posts')}
                  aria-pressed={pane === 'posts'}
                >
                  Posts
                  {draftPosts.length > 0 && (
                    <span
                      className="build-report-subtab__badge"
                      title={`${draftPosts.length} draft${draftPosts.length === 1 ? '' : 's'} left to review`}
                    >
                      {draftPosts.length}
                    </span>
                  )}
                </button>
                <button
                  type="button"
                  className={`build-report-subtab ${pane === 'details' ? 'is-active' : ''}`}
                  data-help="reports.details"
                  onClick={() => setPaneChoice('details')}
                  aria-pressed={pane === 'details'}
                >
                  Report Details
                </button>
                {/* Last, and only while the preview has no column of its own.
                    Inserting it FIRST shifted Posts and Report Details along by
                    one whenever the window narrowed, so the same two tabs sat in
                    different places depending on width — and the strip opened
                    with its second tab active, since `pane` defaults to posts.
                    Trailing it also follows the order of the work: publish the
                    posts, fill in the details, then check the result. */}
                {isNarrow && (
                  <button
                    type="button"
                    className={`build-report-subtab ${pane === 'preview' ? 'is-active' : ''}`}
                    onClick={() => setPaneChoice('preview')}
                    aria-pressed={pane === 'preview'}
                  >
                    Preview
                  </button>
                )}
              </div>

              {pane === 'posts' && (
                <div className="pill-toolbar__segment build-report-header__filter" role="group" aria-label="Filter posts by status" data-help="reports.post-filter">
                  <button type="button" className={filter === 'all' ? 'is-active' : ''} onClick={() => setFilterChoice('all')} aria-pressed={filter === 'all'}>
                    All ({posts.length})
                  </button>
                  <button type="button" className={`is-draft ${filter === 'draft' ? 'is-active' : ''}`} onClick={() => setFilterChoice('draft')} aria-pressed={filter === 'draft'}>
                    Draft ({draftPosts.length})
                  </button>
                  <button type="button" className={filter === 'published' ? 'is-active' : ''} onClick={() => setFilterChoice('published')} aria-pressed={filter === 'published'}>
                    Published ({publishedPosts.length})
                  </button>
                </div>
              )}
            </div>

            {pane === 'posts' ? (
              <>
                <div ref={queuePaneRef} data-help="reports.posts-list" className="layout-scrollable-content px-3 pb-3 pt-2" style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
                  {listPosts.length === 0 ? (
                    <div className="text-muted text-center py-5">
                      {filter === 'draft'
                        ? 'No drafts left to review — every post in this period has been published.'
                        : filter === 'published'
                          ? 'Nothing published yet for this period.'
                          : 'No posts found for this period.'}
                      {/* Each empty state has a different next step: nothing
                          written yet, nothing published yet, or nothing left to
                          do — which is a cue to generate, not a problem. */}
                      <div className="mt-3">
                        {filter === 'draft' ? (
                          <HelpAsk guideKey="reports.generate-pdf" label="How do I generate the report?" variant="inline" />
                        ) : filter === 'published' ? (
                          <HelpAsk guideKey="posts.publish-post" label="How do I publish a post?" variant="inline" />
                        ) : (
                          <HelpAsk guideKey="posts.create-post" label="How do I write a post?" variant="inline" />
                        )}
                      </div>
                    </div>
                  ) : (
                    listPosts.map((p, i) => {
                      // A published card previews the public record (the approved
                      // wording — possibly the AI rewrite — and only the photos the
                      // report will render); a draft previews the original, with
                      // excluded photos dimmed. The overlay shows both when opened.
                      const cardText = (p.is_published ? p.published_text ?? p.text_content : p.text_content) ?? '';
                      const cardUploads = (p.uploads ?? []).filter(u => !p.is_published || !u.excluded_from_report);
                      const stamp = postStamp(p.post_date);
                      return (
                      // Same card dialect as the Posts grid — photo strip on
                      // top, date masthead, writing that fades where it
                      // overruns — at the shorter --review size, because this
                      // list is a queue to work through rather than a gallery
                      // to browse. What it shows still differs deliberately:
                      // a published card previews the report's version.
                      <div
                        key={p.id}
                        ref={reviewPost?.id === p.id ? selectedCardRef : undefined}
                        className={`card post-card post-card--review mb-2 position-relative ${reviewPost?.id === p.id ? 'selected' : ''} ${p.is_published ? 'post-published' : 'post-draft'}`}
                        style={{ cursor: 'pointer', minWidth: 0, animationDelay: `${Math.min(i, 10) * 40}ms` }}
                        onClick={() => setReviewPostId(p.id)}
                        title={p.is_published
                          ? (ALLOW_EDITING_PUBLISHED_POSTS ? 'Review the published post again' : 'View the published post')
                          : 'Review & publish this post'}
                      >
                        {cardUploads.length > 0 && (
                          <ReviewPhotoSheet uploads={cardUploads} eager={i < 4} />
                        )}
                        {/* Wrapped so the writing can stand beside the photos
                            as a column once the card is wide enough. */}
                        <div className="post-card__main">
                          <div className="post-card__head">
                            <span className={`post-status-badge ${p.is_published ? 'published' : 'draft'}`}>
                              {p.is_published ? 'Published' : 'Draft'}
                            </span>
                            <span className="post-card__day">{stamp.day}</span>
                            {/* How many photos there are, stated rather than
                                overlaid: the strip's tile count varies with the
                                column width, so a "+N" badge couldn't stay true. */}
                            {cardUploads.length > 0 && (
                              <span className="post-card__photos" title={`${cardUploads.length} photo${cardUploads.length === 1 ? '' : 's'} in this post`}>
                                <i className="fas fa-image" />{cardUploads.length}
                              </span>
                            )}
                            <span className="post-card__time">{stamp.time}</span>
                          </div>
                          <p className="post-card__body">{cardText}</p>
                        </div>
                      </div>
                      );
                    })
                  )}
                </div>
              </>
            ) : pane === 'preview' ? (
              // The same preview node the left pane renders when there's room
              // for it — .report-container brings its own scrolling.
              <div className="build-report-preview">{previewSheet}</div>
            ) : (
              <div className="layout-scrollable-content px-3 pb-3 pt-3" style={{ flex: 1, overflowY: 'auto' }}>
                <ActionPanel editableFields={editableFields} fieldDefaults={fieldDefaults} />
              </div>
            )}
            </div>

            {/* Press bay — chrome for the report FILE, so it belongs to the
                column rather than to either pane. Whichever pane is open, the
                reviewer can see whether a report exists, whether it has gone
                stale, and generate or open it. */}
            <GenerateReportPanel
              childId={childId}
              startDate={startDate ?? null}
              endDate={endDate ?? null}
              posts={publishedPosts}
              // Drafts in this period. The panel itself only ever sees the
              // PUBLISHED posts (they're all a report contains), so it can't
              // count what's being left out without being told.
              draftCount={posts.length - publishedPosts.length}
              existing={existingReport}
              checkingExisting={checkingExisting}
              onGenerated={(info) => {
                // Generating counts as hydrating this identity. Without saying
                // so, the next post change would re-hydrate from the server and
                // overwrite any field values edited since generating.
                hydratedFor.current = reportIdentity;
                setExistingReport(info);
              }}
              templateId={templateId}
              editableFields={editableFields}
              fieldDefaults={fieldDefaults}
              childName={childName}
              // Always available, including from the details pane itself. It's
              // tempting to suppress it there ("you're already looking at the
              // fields"), but the pre-flight modal COVERS the pane: hiding the
              // action meant the dialog could name an empty field and offer no
              // way to go and deal with it. Switching to a pane you're already
              // on is a harmless no-op; leaving the reader stuck is not.
              onEditFields={() => setPaneChoice('details')}
              onGenerateSuccess={onNotice}
            />
          </div>
        )}

        {reviewPost && (
          <ReviewPublishOverlay
            key={reviewPost.id}
            post={reviewPost}
            // Stepping through the list as it currently stands, not as it was
            // when the overlay opened: publishing a draft while filtered to
            // Draft takes it out of the list, and the overlay should agree with
            // what's behind it rather than walk a queue that no longer exists.
            // Undefined at each end, so the buttons disable there — a review
            // queue you can loop around forever never answers "am I done".
            position={reviewIndex >= 0 ? { index: reviewIndex, total: listPosts.length } : undefined}
            onPrev={reviewIndex > 0
              ? () => setReviewPostId(listPosts[reviewIndex - 1].id)
              : undefined}
            onNext={reviewIndex >= 0 && reviewIndex < listPosts.length - 1
              ? () => setReviewPostId(listPosts[reviewIndex + 1].id)
              : undefined}
            onClose={() => setReviewPostId(null)}
            onReloadPosts={onReloadPosts}
            onPublished={handlePublished}
            onUnpublished={async (msg) => {
              onNotice(msg);
              // Keep the overlay open: the post reloads as a draft, so the
              // reviewer can fix what prompted the unpublish (wording, a photo
              // exclusion) and republish without reopening the post.
              await onReloadPosts(true);
            }}
          />
        )}
      </div>
    </ReportProvider>
  );
};

export default BuildReportPanel;
